import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, ChevronRight, FileDown, Plus } from 'lucide-react';
import { watchExperiments } from '../services/experiments';
import type { Experiment } from '../services/experiments';
import type { Project } from '../lib/types';
import type { Reading } from '../services/readings';
import { Button } from './ui';
import ExperimentDialog from './ExperimentDialog';

/**
 * The project's notebook: its experiments, newest first, each a dated entry
 * holding notes, attached documents and the data captured in it.
 */
export default function NotebookSection({
  project,
  readings,
  readOnly,
}: {
  project: Project;
  readings: Reading[];
  readOnly: boolean;
}) {
  const [experiments, setExperiments] = useState<Experiment[] | null>(null);
  const [creating, setCreating] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => watchExperiments(project.id, setExperiments), [project.id]);

  const dataCount = (id: string) => readings.filter((r) => r.experimentId === id).length;

  async function exportPdf() {
    if (!experiments) return;
    setExporting(true);
    setError(null);
    try {
      const { exportNotebookDocx } = await import('../services/exportNotebook');
      await exportNotebookDocx(project, experiments, readings, project.readers ?? []);
    } catch (e) {
      setError((e as Error)?.message ?? 'Could not build the Word file.');
    } finally {
      setExporting(false);
    }
  }

  return (
    <section className="mb-6">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-base font-semibold">
          <BookOpen size={16} className="text-accent-400" />
          Notebook
        </h2>
        <div className="flex gap-2">
          {(experiments?.length ?? 0) > 0 && (
            <Button
              variant="ghost"
              icon={<FileDown size={14} />}
              loading={exporting}
              onClick={() => void exportPdf()}
            >
              Notebook (Word)
            </Button>
          )}
          {!readOnly && (
            <Button variant="secondary" icon={<Plus size={14} />} onClick={() => setCreating(true)}>
              New experiment
            </Button>
          )}
        </div>
      </div>

      {error && <p className="mb-2 text-sm text-red-300">{error}</p>}

      {experiments === null ? null : experiments.length === 0 ? (
        <p className="rounded-lg border border-dashed border-ink-700 px-4 py-5 text-center text-sm text-ink-400">
          {readOnly
            ? 'No experiments yet.'
            : 'Start an experiment to keep its notes, documents and data together under one date.'}
        </p>
      ) : (
        <ul className="space-y-1.5">
          {experiments.map((e) => {
            const n = dataCount(e.id);
            return (
              <li key={e.id}>
                <Link
                  to={`/p/${project.id}/e/${e.id}`}
                  className="flex items-center gap-3 rounded-lg border border-ink-700 bg-ink-800/40 px-3 py-2.5 transition-colors hover:border-ink-600 hover:bg-ink-800 pointer-coarse:min-h-11"
                >
                  <span className="w-24 shrink-0 text-xs tabular-nums text-ink-400">{shortDate(e.date)}</span>
                  <span className="min-w-0 flex-1 truncate text-sm font-medium">{e.title || 'Untitled'}</span>
                  {n > 0 && (
                    <span className="shrink-0 text-xs text-ink-400">
                      {n} reading{n === 1 ? '' : 's'}
                    </span>
                  )}
                  <ChevronRight size={16} className="shrink-0 text-ink-500" />
                </Link>
              </li>
            );
          })}
        </ul>
      )}

      <ExperimentDialog
        projectId={project.id}
        target={creating ? 'new' : null}
        onClose={() => setCreating(false)}
      />
    </section>
  );
}

export function shortDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}
